import { useState } from 'react';
import { X, CheckCircle2, AlertCircle } from 'lucide-react';

interface CompletionApprovalModalProps {
  jobId: string;
  totalPayout: string;
  getQAProgress: () => { completed: number; total: number };
  qaItems: { name: string; completed: boolean }[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (releasePayload: boolean, internalNote: string) => void;
}

const CompletionApprovalModal = ({
  jobId,
  totalPayout,
  getQAProgress,
  qaItems,
  isOpen,
  onClose,
  onConfirm,
}: CompletionApprovalModalProps) => {
  const [releasePayload, setReleasePayload] = useState(true);
  const [internalNote, setInternalNote] = useState('');

  const handleConfirm = () => {
    onConfirm(releasePayload, internalNote);
    handleClose();
  };

  const handleClose = () => {
    setReleasePayload(true);
    setInternalNote('');
    onClose();
  };

  const { completed, total } = getQAProgress();
  const progressPercentage = Math.round((completed / total) * 100);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Completion Approval</h2>
              <p className="text-xs text-gray-600 mt-1">Job ID: {jobId}</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* QA Checklist Summary Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">QA Checklist Summary</h3>
            <div className={`px-4 py-1 rounded-full font-bold text-sm ${
              progressPercentage === 100
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              {progressPercentage}% COMPLETE
            </div>
          </div>

          {/* QA Items Grid */}
          <div className="grid grid-cols-2 gap-4">
            {qaItems.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {item.completed ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex-shrink-0 mt-0.5"></div>
                )}
                <span className={`text-sm font-medium ${item.completed ? 'text-gray-900' : 'text-gray-500'}`}>
                  {item.name}
                </span>
              </div>
            ))}
          </div>

          {/* Release Payout */}
          <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <input
              type="checkbox"
              id="releasePayload"
              checked={releasePayload}
              onChange={(e) => setReleasePayload(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 mt-1"
            />
            <div className="flex-1">
              <label htmlFor="releasePayload" className="block font-semibold text-gray-900 text-sm mb-1">
                Release Payout
              </label>
              <p className="text-xs text-gray-600">Authorize funds transfer to provider account</p>
            </div>
          </div>

          {/* Total Payout Display */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-600 p-6 rounded">
            <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">Total Payout</p>
            <p className="text-4xl font-bold text-gray-900">{totalPayout}</p>
          </div>

          {/* Internal Completion Note */}
          <div>
            <label className="block text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">
              Internal Completion Note
            </label>
            <textarea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder="Add any internal remarks or audit findings here..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              rows={4}
            />
          </div>

          {/* Info Banner */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-blue-700">Completion triggers final audit</p>
              <p className="text-xs text-blue-600 mt-1">Job status will be marked as completed and locked</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between gap-3">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-gray-600 font-semibold hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            Approve & Close Job
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompletionApprovalModal;
